import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal", "paymentMethodInput",
    "idealOption", "cardOption",
    "vatInput", "vatNumberHidden", "vatExemptInput", "vatValidateButton", "vatStatus",
    "breakdownBase", "breakdownSurcharge", "breakdownSurchargeRow",
    "breakdownVat", "breakdownVatLabel", "breakdownTotal"
  ]
  static values = {
    coursePrice: Number,
    cardSurchargeRate: { type: Number, default: 0.04 },
    vatRate: { type: Number, default: 0.21 },
    validateVatUrl: String
  }

  open(event) {
    const courseId = event.currentTarget.dataset.courseId
    const coursePrice = parseFloat(event.currentTarget.dataset.coursePrice)

    this.coursePriceValue = coursePrice
    this.modalTarget.querySelector("[data-field='course-id']").value = courseId

    this.vatExempt = false
    this.vatInputTarget.value = ""
    this.vatNumberHiddenTarget.value = ""
    this.vatExemptInputTarget.value = "0"
    this.vatStatusTarget.innerHTML = ""

    this.selectMethod("ideal")

    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  close() {
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) this.close()
  }

  selectIdeal() {
    this.selectMethod("ideal")
  }

  selectCard() {
    this.selectMethod("creditcard")
  }

  selectMethod(method) {
    this.paymentMethodInputTarget.value = method

    if (method === "ideal") {
      this.idealOptionTarget.classList.add("ring-2", "ring-indigo-500", "bg-indigo-50")
      this.idealOptionTarget.classList.remove("bg-white")
      this.cardOptionTarget.classList.remove("ring-2", "ring-indigo-500", "bg-indigo-50")
      this.cardOptionTarget.classList.add("bg-white")
    } else {
      this.cardOptionTarget.classList.add("ring-2", "ring-indigo-500", "bg-indigo-50")
      this.cardOptionTarget.classList.remove("bg-white")
      this.idealOptionTarget.classList.remove("ring-2", "ring-indigo-500", "bg-indigo-50")
      this.idealOptionTarget.classList.add("bg-white")
    }

    this.updateBreakdown()
  }

  updateBreakdown() {
    const base = this.coursePriceValue
    const isCreditCard = this.paymentMethodInputTarget.value === "creditcard"
    const ccSurcharge = isCreditCard ? +(base * this.cardSurchargeRateValue).toFixed(2) : 0
    const vat = this.vatExempt ? 0 : +(base * this.vatRateValue).toFixed(2)
    const total = +(base + ccSurcharge + vat).toFixed(2)

    this.breakdownBaseTarget.textContent = `\u20AC${base.toFixed(2)}`
    this.breakdownSurchargeTarget.textContent = `\u20AC${ccSurcharge.toFixed(2)}`
    this.breakdownSurchargeRowTarget.classList.toggle("hidden", !isCreditCard)
    this.breakdownVatTarget.textContent = `\u20AC${vat.toFixed(2)}`
    this.breakdownTotalTarget.textContent = `\u20AC${total.toFixed(2)}`

    if (this.vatExempt) {
      this.breakdownVatLabelTarget.innerHTML = this.breakdownVatLabelTarget.dataset.exemptLabel || "VAT (reverse charge)"
    } else {
      this.breakdownVatLabelTarget.innerHTML = this.breakdownVatLabelTarget.dataset.defaultLabel || this.breakdownVatLabelTarget.textContent
    }
  }

  async validateVat() {
    const vatNumber = this.vatInputTarget.value.trim()
    if (!vatNumber) return

    this.vatValidateButtonTarget.disabled = true
    this.vatStatusTarget.innerHTML = '<span class="text-gray-500">Validating...</span>'

    try {
      const response = await fetch(this.validateVatUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ vat_number: vatNumber })
      })

      const data = await response.json()

      if (data.valid) {
        this.vatExempt = true
        this.vatNumberHiddenTarget.value = vatNumber
        this.vatExemptInputTarget.value = "1"
        const companyInfo = data.company_name ? ` \u2014 ${data.company_name}` : ""
        this.vatStatusTarget.innerHTML = `<span class="text-green-600">\u2713 Valid${companyInfo}</span>`
      } else if (data.vies_unavailable) {
        this.vatExempt = false
        this.vatExemptInputTarget.value = "0"
        this.vatStatusTarget.innerHTML = '<span class="text-amber-600">VIES unavailable \u2014 VAT will be charged</span>'
      } else {
        this.vatExempt = false
        this.vatExemptInputTarget.value = "0"
        this.vatStatusTarget.innerHTML = `<span class="text-red-600">${data.error || "Invalid VAT number"}</span>`
      }
    } catch {
      this.vatExempt = false
      this.vatExemptInputTarget.value = "0"
      this.vatStatusTarget.innerHTML = '<span class="text-red-600">Validation failed</span>'
    }

    this.vatValidateButtonTarget.disabled = false
    this.updateBreakdown()
  }

  clearVat() {
    if (this.vatExempt) {
      this.vatExempt = false
      this.vatNumberHiddenTarget.value = ""
      this.vatExemptInputTarget.value = "0"
      this.vatStatusTarget.innerHTML = ""
      this.updateBreakdown()
    }
  }
}
